<!-- Component to add code editing like behaviour to Field Calculation formulae/expressions etc. -->
<template>
  <div ref="editorContainer" class="code-editor-container">
    <!-- CodeMirror editor will be mounted here. -->
    <b-button class="clear-icon text-info" v-if="documentLength && !readOnly && showClearIcon" variant="outline" size="md"
      @click="setEditorDoc('')">
      <font-awesome-icon :icon="['fas', 'close']" />
    </b-button>
  </div>
</template>

<script>

/* https://codemirror.net/ */
import { minimalSetup, basicSetup } from 'codemirror';
import { EditorState } from "@codemirror/state"
import { EditorView, lineNumbers } from "@codemirror/view"
/* https://www.npmjs.com/package/@codemirror/lang-javascript */
import { javascriptLanguage, javascript } from "@codemirror/lang-javascript"

export default {

  name: 'CodeEditor',

  props: {
    document: { type: String, default: '' },
    language: { type: String, default: '' },
    showClearIcon: { type: Boolean, default: false },
    editable: { type: Boolean, default: true },
    readOnly: { type: Boolean, default: false },
  },

  data() {
    return {
      editorView: null,
      documentLength: (this.document || '').length,
    };
  },

  watch: {
    /** Refresh Editor View if document changes. */
    document(val) {
      if (this.editorView && this.getEditorDoc() != val) {
        this.setEditorDoc(val)
      }
    },
  },

  methods: {

    /** Initialise the code editor. */
    initEditor(doc) {

      if (this.editorView) this.editorView.destroy();

      // Predefined extension combinations:
      // Both minimalSetup and basicSetup include the following:
      // - defaultKeymap, history, highlightSpecialChars, drawSelection, defaultHighlightStyle.
      // basicSetup also includes:
      // - lineNumbers, foldGutter, dropCursor, allowMultipleSelections, indentOnInput, bracketMatching, closeBrackets, autocompletion,
      // - rectangularSelection, crosshairCursor, highlightActiveLine, highlightActiveLineGutter, highlightSelectionMatches, searchKeymap, lintKeymap)
      // Getting some odd results with the basicSetup, specifically duplication of brackets, so going with minimalSetup and cherry-picking extensions.

      const editorParent = this.$refs.editorContainer;
      const onDocChanged = EditorState.transactionExtender.of(tr => { return this.onEditorDocChanged(tr); });
      const extensions = [minimalSetup, lineNumbers(), EditorView.lineWrapping, onDocChanged];
      if (!this.editable) extensions.push([EditorView.editable.of(false)]);
      if (this.readOnly) extensions.push([EditorState.readOnly.of(true)]);
      if (/javascript/i.test(this.language)) extensions.push(javascriptLanguage);
      this.editorView = new EditorView({
        parent: editorParent,
        doc: doc || '',
        extensions: extensions
      });
      this.$emit('doc-changed', this.getEditorDoc());
      this.documentLength = this.getEditorDocLength();
    },

    onEditorDocChanged(tr) {
      if (!tr.docChanged) return null;
      const doc = tr.state.doc.toString();
      this.documentLength = doc.length;
      this.$emit('doc-changed', doc);
      return null;
    },

    // Editor properties.

    getEditorView() {
      return this.editorView;
    },

    // Document.

    getEditorDocLength() {
      return this.editorView.state.doc.length;
    },

    getEditorDoc() {
      return this.editorView.state.doc.toString();
    },

    // Update document.

    setEditorDoc(doc) {
      if (!this.editorView) return;
      if (!doc || doc != this.getEditorDoc()) {
        this.initEditor(doc);
        this.$nextTick(() => {
          this.editorView.focus();
        });
      }
    },

    replaceTextSelection(text, cursorOffset = 0) {
      const editor = this.editorView;
      // Replace text.
      editor.dispatch(editor.state.replaceSelection(text));

      // Wait until the editor is updated before setting the cursor position.
      this.$nextTick(() => {
        if (cursorOffset != 0)
          editor.dispatch({ selection: { anchor: editor.state.selection.main.head + cursorOffset } });
        editor.focus();
      });
    },

  },

  mounted() {
    this.initEditor(this.document);
  },

  beforeDestroy() {
    if (this.editorView) {
      this.editorView.destroy()
    }
  },

};
</script>

<style lang="scss">
.code-editor-container {

  border: 1px solid #ced4da;
  border-radius: 3px;

  .cm-editor {
    height: 100%;
  }

  .cm-scroller {
    background-color: #ffffff;
    overflow: auto;
  }

  .cm-gutters,
  .cm-gutter {
    color: inherit;
  }
}

button.clear-icon {
  position: absolute;
  top: -6px;
  right: 8px;
}
</style>